import { createContext, useContext, useState, useCallback, ReactNode, RefObject } from "react";
import { FlyingWDollars } from "@/components/animations/FlyingWDollars";

interface WDollarAnimation {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  count: number;
}

interface WDollarAnimationContextType {
  triggerWDollarAnimation: (sourceElement: HTMLElement, amount: number) => void;
  registerWDollarDestination: (ref: RefObject<HTMLElement>) => void;
}

const WDollarAnimationContext = createContext<WDollarAnimationContextType | undefined>(undefined);

export function WDollarAnimationProvider({ children }: { children: ReactNode }) {
  const [animations, setAnimations] = useState<WDollarAnimation[]>([]);
  const [destinationRef, setDestinationRef] = useState<RefObject<HTMLElement> | null>(null);

  const registerWDollarDestination = useCallback((ref: RefObject<HTMLElement>) => {
    setDestinationRef(ref);
  }, []);

  const triggerWDollarAnimation = useCallback((sourceElement: HTMLElement, amount: number) => {
    if (!destinationRef?.current) return;

    const sourceRect = sourceElement.getBoundingClientRect();
    const destRect = destinationRef.current.getBoundingClientRect();

    const coinCount = Math.min(Math.max(Math.ceil(amount / 0.05), 1), 6);

    const newAnimation: WDollarAnimation = {
      id: crypto.randomUUID(),
      startX: sourceRect.left + sourceRect.width / 2,
      startY: sourceRect.top + sourceRect.height / 2,
      endX: destRect.left + destRect.width / 2,
      endY: destRect.top + destRect.height / 2,
      count: coinCount,
    };

    setAnimations((prev) => [...prev, newAnimation]);
  }, [destinationRef]);

  const handleAnimationComplete = useCallback((id: string) => {
    setAnimations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return (
    <WDollarAnimationContext.Provider value={{ triggerWDollarAnimation, registerWDollarDestination }}>
      {children}
      <FlyingWDollars animations={animations} onAnimationComplete={handleAnimationComplete} />
    </WDollarAnimationContext.Provider>
  );
}

export function useWDollarAnimation() {
  const context = useContext(WDollarAnimationContext);
  if (!context) {
    throw new Error("useWDollarAnimation must be used within WDollarAnimationProvider");
  }
  return context;
}
